// Order/message parsing and duplicate-order detection.
//
// `parseMessage` is pure (no ORDERS/PRODUCTS/SETTINGS dependency at all —
// it only ever needed the geo/phone/text utilities already in src/utils/,
// so unlike pricing.js/inventory.js/wallet.js it doesn't need dependency
// injection for app state). The duplicate-detection functions do read the
// mutable ORDERS/PRODUCTS globals in the original, so — same as the other
// services — they take `orders`/`products` as explicit arguments here.
//
// Extracted from public/index.html's inline script (Phase 2 modularization,
// slice 5). public/index.html keeps a one-line wrapper of the same original
// name for every function here. See REFACTOR_SECURITY_REPORT.md, Phase 2
// section, for the before/after behavioral-equivalence checks run before
// each function was removed from the inline script.
import { norm } from '../utils/text.js';
import { convertDigits, findPhones, normalizePhone } from '../utils/phone.js';
import { CITY_DETECT, LANDMARK_WORDS, extractLandmarkSegment, stripLeadingCity } from '../utils/geo.js';
import { getOrderItems, orderTotal } from './pricing.js';

// Extracts name/phone/phone2/city/address/landmark from a pasted
// Instagram/WhatsApp order message (French or Arabic). `flags` marks which
// fields the extraction wasn't confident about, for the "à vérifier" UI
// badges.
export function parseMessage(text) {
  const res = { name: '', phone: '', phone2: '', city: '', address: '', landmark: '', flags: {} };
  if (!text || !text.trim()) { res.flags = { name: 1, phone: 1, city: 1, address: 1 }; return res; }
  const original = convertDigits(text.replace(/\r/g, ''));
  const flat = original.replace(/\n+/g, ' ').replace(/\s+/g, ' ').trim();
  const nflat = norm(flat);
  const SEP = '[\\s,،.;:/\\-]';
  const phones = findPhones(original);
  if (phones[0]) res.phone = phones[0].value; else res.flags.phone = 1;
  if (phones[1]) res.phone2 = phones[1].value;
  let cityMatch = null;
  for (const c of CITY_DETECT) {
    const pat = c.pat.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
    const re = new RegExp('(^|' + SEP + ')(' + pat + ')(' + SEP + '|$)');
    const mm = re.exec(nflat);
    if (mm) { const start = mm.index + mm[1].length; if (!cityMatch || start < cityMatch.start) { cityMatch = { label: c.canon, pat: c.pat, start, end: start + c.pat.length }; } }
  }
  if (cityMatch) res.city = cityMatch.label; else res.flags.city = 1;
  let lmStart = -1, lmWord = '';
  for (const w of LANDMARK_WORDS) {
    const nw = norm(w);
    const idx = nflat.indexOf(nw);
    if (idx > -1 && (idx === 0 || new RegExp(SEP).test(nflat[idx - 1]))) { if (lmStart === -1 || idx < lmStart) { lmStart = idx; lmWord = w; } }
  }
  const nphoneIdx = phones[0] ? norm(flat).indexOf(norm(phones[0].raw.trim())) : -1;
  const cand = [];
  if (cityMatch) cand.push(cityMatch.start);
  if (lmStart > -1) cand.push(lmStart);
  if (nphoneIdx > -1) cand.push(nphoneIdx);
  const fd = nflat.search(/\d/);
  if (fd > -1) cand.push(fd);
  let cut = cand.length ? Math.min(...cand) : nflat.length;
  let namePart = flat.slice(0, cut).trim().replace(/[,،;:\-–]+$/, '').trim().replace(/^(nom|client|mr|mme|m\.|madame|monsieur|الاسم|الإسم)\s*[:\-]?\s*/i, '').trim();
  const nt = namePart.split(/\s+/).filter(Boolean);
  if (namePart && nt.length >= 1 && nt.length <= 4 && !/\d/.test(namePart)) res.name = namePart; else { res.name = namePart || ''; res.flags.name = 1; }
  let addr = flat.slice(cut).trim();
  phones.forEach(p => { addr = addr.replace(p.raw.trim(), ' '); });
  addr = addr.replace(/\s{2,}/g, ' ').replace(/^[,،;:\-–\s]+/, '').replace(/[,،;:\-–\s]+$/, '').trim();
  if (cityMatch) addr = stripLeadingCity(addr, [cityMatch.label, cityMatch.pat]);
  addr = addr.replace(/^[,،;:\-–\s]+/, '').replace(/[,،;:\-–\s]+$/, '').trim();
  res.address = addr; if (!addr) res.flags.address = 1;
  if (lmWord && addr) res.landmark = extractLandmarkSegment(addr, lmWord) || '';
  return res;
}

// ---------- duplicate-order detection ----------

export function findByPhone(orders, phone, ex) {
  if (!phone) return [];
  return orders.filter(o => o.id !== ex && (o.phone === phone || o.phone2 === phone));
}

export function customerStats(orders, phone) {
  const l = findByPhone(orders, phone);
  return { count: l.length, delivered: l.filter(o => o.status === 'livree').length, bad: l.filter(o => ['refusee', 'retournee'].includes(o.status)).length, list: l };
}

export function duplicateSignature(products, o) {
  const items = getOrderItems(products, o).map(i => [String(i.productId || norm(i.product)), Number(i.qty || 0), Number(i.unitPrice || 0)].join(':')).sort().join('|');
  return [normalizePhone(o.phone).value, o.orderDate || '', items, Number(orderTotal(products, o))].join('~');
}

export function exactDuplicateOf(orders, products, o, excludeId) {
  const sig = duplicateSignature(products, o);
  return orders.find(x => x.id !== excludeId && duplicateSignature(products, x) === sig);
}

// Newest-first comparator (Array.prototype.sort callback), used by both
// viewDashboard's "dernières commandes" list and viewOrders' filtering
// (viewOrders itself isn't extracted yet — this moved here, ahead of it,
// so both call the same module function once it is). Slice 11.
export function byNew(a, b) {
  return (b.createdAt || '').localeCompare(a.createdAt || '');
}
